import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import { Seo } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { Heart, PenLine, ImagePlus, Trash2, Clock, HeartHandshake, Users, PartyPopper, Pin } from "lucide-react";
import { ReportButton } from "@/components/ReportButton";
import { TierBadge } from "@/components/TierBadge";
import { RoleBadge } from "@/components/RoleBadge";
import { toast } from "sonner";

export const STORY_TYPE_META = {
  impact: { label: "Impact story", chip: "Impact", icon: HeartHandshake, color: "text-forest border-forest/20 bg-forest/5" },
  neighbour_help: { label: "Neighbour help", chip: "Neighbour help", icon: Users, color: "text-terracotta border-terracotta/25 bg-terracotta/5" },
  gathering: { label: "Gathering recap", chip: "Gatherings", icon: PartyPopper, color: "text-amber-700 border-amber-600/25 bg-amber-500/5" },
};

const FILTERS = [
  ["", "All"],
  ["impact", "Impact"],
  ["neighbour_help", "Neighbour help"],
  ["gathering", "Gatherings"],
];

function StoryCard({ s, user, onLike, onDelete, onPin, i, highlighted }) {
  const liked = user && s.likes?.includes(user.user_id);
  const typeMeta = STORY_TYPE_META[s.story_type] || STORY_TYPE_META.impact;
  const ref = useRef(null);
  useEffect(() => {
    // deep-linked target (?story=<id>) scrolls into view once rendered
    if (highlighted) ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlighted]);
  return (
    <article ref={ref} data-testid={`story-card-${s.id}`} className={`bg-white border rounded-2xl overflow-hidden animate-fade-up ${highlighted ? "border-terracotta ring-2 ring-terracotta/30" : "border-neutral-200"}`} style={{ animationDelay: `${i * 70}ms` }}>
      {s.image && <img src={imgSrc(s.image)} alt="" className="w-full h-56 object-cover" />}
      <div className="p-6">
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={imgSrc(s.user_picture)} alt={s.user_name} />
            <AvatarFallback className="bg-forest text-white">{s.user_name?.[0]}</AvatarFallback>
          </Avatar>
          <div>
            <Link to={`/u/${s.user_id}`} className="font-medium text-forest hover:underline">{s.user_name}</Link>
            <TierBadge hours={s.author_total_hours} className="ml-2" />
            {s.role_badge && <RoleBadge badge={s.role_badge} className="ml-1.5" />}
            {s.hours ? (
              <div className="text-xs text-neutral-500">
                {s.hours}h with <Link to={`/o/${s.org_user_id}`} className="text-forest hover:underline">{s.org_name}</Link>
              </div>
            ) : (
              <div className="text-xs text-neutral-500">{typeMeta.label}</div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Badge data-testid={`story-type-chip-${s.id}`} variant="outline" className={`rounded-full text-[10px] uppercase tracking-wide ${typeMeta.color}`}>
              <typeMeta.icon className="w-3 h-3 mr-1" /> {typeMeta.chip}
            </Badge>
            <span className="text-xs text-neutral-400">{new Date(s.created_at).toLocaleDateString()}</span>
          </div>
        </div>
        <p className="text-neutral-700 mt-4 leading-relaxed">{s.text}</p>
        {s.opportunity_title && (
          <Badge variant="outline" className="rounded-full text-[11px] mt-4 border-forest/20 text-forest">{s.opportunity_title}</Badge>
        )}
        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-neutral-100">
          <button
            data-testid={`story-like-${s.id}`}
            onClick={() => onLike(s)}
            className={`inline-flex items-center gap-1.5 text-sm transition-colors ${liked ? "text-terracotta" : "text-neutral-500 hover:text-terracotta"}`}
          >
            <Heart className={`w-4 h-4 ${liked ? "fill-terracotta" : ""}`} /> {s.likes?.length || 0}
          </button>
          <ShareButton
            testId={`story-share-${s.id}`}
            imageUrl={api.ogStoryUrl(s.id)}
            shareUrl={api.shareUrl("story", s.id)}
            title="An impact story on CivicHearth"
            text={`"${(s.text || "").slice(0, 120)}${(s.text || "").length > 120 ? "…" : ""}" — ${s.user_name} on CivicHearth`}
            waText={`Worth a read — ${s.user_name}'s volunteering story on CivicHearth:`}
            filename="civichearth-story.png"
            label="Share"
            variant="ghost"
            className="h-8 px-2 text-neutral-500"
          />
          {user && user.user_id !== s.user_id && (
            <ReportButton targetType="story" targetId={s.id} label="" />
          )}
          {user?.user_id === s.org_user_id && (
            // the org this impact story is about can pin it to /o/:id
            <button
              data-testid={`story-pin-${s.id}`}
              onClick={() => onPin(s)}
              className={`inline-flex items-center gap-1.5 text-sm transition-colors ${s.org_pinned ? "text-terracotta hover:text-neutral-500" : "text-neutral-500 hover:text-forest"}`}
            >
              <Pin className={`w-4 h-4 ${s.org_pinned ? "fill-terracotta/20" : ""}`} />
              {s.org_pinned ? "Unpin from our page" : "Pin to our page"}
            </button>
          )}
          {user?.user_id === s.user_id && (
            <button data-testid={`story-delete-${s.id}`} onClick={() => onDelete(s)} className="ml-auto text-neutral-400 hover:text-red-500" aria-label="Delete story">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Stories() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [stories, setStories] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoaded, setLogsLoaded] = useState(false);
  const [logId, setLogId] = useState("");
  const [storyType, setStoryType] = useState("impact");
  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const handledStoryRef = useRef(null);

  useEffect(() => {
    api.stories(30, filter || undefined).then(fetched => setStories(prev => {
      // keep a deep-linked story pinned even when it's older than the
      // 30-item feed window or filtered out
      const pinned = prev.find(x => x.id === handledStoryRef.current && !fetched.some(f => f.id === x.id));
      return pinned ? [pinned, ...fetched] : fetched;
    })).catch(() => {});
  }, [filter]);

  // Deep link from the org notification: /stories?story=<id> pins, scrolls to
  // and highlights that story; deleted stories fail gracefully with a toast.
  useEffect(() => {
    const sid = sp.get("story");
    if (!sid || handledStoryRef.current === sid) return;
    handledStoryRef.current = sid;
    api.story(sid)
      .then(s => {
        setHighlightId(sid);
        setStories(list => list.some(x => x.id === s.id) ? list : [s, ...list]);
      })
      .catch(() => toast("That story is no longer available — it may have been deleted."));
  }, [sp]);

  useEffect(() => {
    if (user && user.user_type !== "organisation") {
      api.myHours()
        .then(h => setLogs(h.filter(x => x.status === "approved")))
        .catch(() => {})
        .finally(() => setLogsLoaded(true));
    }
  }, [user]);

  // Deep link from the post-approval story nudge: /stories?compose=impact&log=<id>
  // opens the composer with that hour log preselected (idempotent — re-runs
  // only set the same state again while the params are present).
  useEffect(() => {
    if (!user || user.user_type === "organisation") return;
    if (sp.get("compose") === "impact") {
      setStoryType("impact");
      const logParam = sp.get("log");
      if (logParam) setLogId(logParam);
      setOpen(true);
    }
  }, [user, sp]);

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const res = await api.uploadImage(f);
      setImage(res.url);
      toast.success("Photo added");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const submit = async () => {
    if (!text.trim()) return toast.error("Write a few words first");
    try {
      const s = await api.postStory({ text, image, story_type: storyType, hour_log_id: storyType === "impact" ? (logId || null) : null });
      setStories(list => (!filter || filter === storyType) ? [s, ...list] : list);
      setOpen(false); setText(""); setImage(""); setLogId(""); setStoryType("impact");
      toast.success("Story shared!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post story");
    }
  };

  const like = async (s) => {
    if (!user) return nav("/login");
    const res = await api.likeStory(s.id);
    setStories(list => list.map(x => x.id === s.id
      ? { ...x, likes: res.liked ? [...(x.likes || []), user.user_id] : (x.likes || []).filter(id => id !== user.user_id) }
      : x));
  };

  const remove = async (s) => {
    await api.deleteStory(s.id);
    setStories(list => list.filter(x => x.id !== s.id));
    toast("Story removed");
  };

  const togglePin = async (s) => {
    try {
      if (s.org_pinned) {
        await api.unpinStory(s.id);
        toast("Unpinned from your page");
      } else {
        await api.pinStory(s.id);
        toast.success("Pinned to your page — visible on your public profile");
      }
      setStories(list => list.map(x => x.id === s.id ? { ...x, org_pinned: !s.org_pinned } : x));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the pin");
    }
  };

  const canPost = user && user.user_type !== "organisation";

  return (
    <div data-testid="stories-page" className="max-w-3xl mx-auto px-6 py-10">
      <Seo title="Community stories" description="Volunteering impact, neighbourly help and gathering recaps — real moments from your community on CivicHearth." />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Community</div>
          <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">Community stories</h1>
          <p className="text-neutral-600 mt-2 text-sm max-w-md">Volunteering impact, neighbourly help and gathering recaps — real moments from your community.</p>
        </div>
        {canPost ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="share-story-btn" className="rounded-full bg-forest hover:bg-forest-hover">
                <PenLine className="w-4 h-4 mr-1.5" /> Share your story
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="story-dialog">
              <DialogHeader>
                <DialogTitle>Share a story</DialogTitle>
                <DialogDescription>Tell the community what happened — an impact story, a neighbourly moment, or a gathering recap.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STORY_TYPE_META).map(([key, m]) => (
                    <button
                      key={key}
                      type="button"
                      data-testid={`story-type-${key}`}
                      onClick={() => setStoryType(key)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        storyType === key ? "bg-forest text-white border-forest" : "bg-white text-neutral-600 border-neutral-200 hover:border-forest/40"
                      }`}
                    >
                      <m.icon className="w-3.5 h-3.5" /> {m.label}
                    </button>
                  ))}
                </div>
                {storyType === "impact" && (
                  logs.length > 0 ? (
                    <Select value={logId} onValueChange={setLogId}>
                      <SelectTrigger data-testid="story-log-select"><SelectValue placeholder="Which contribution? (latest by default)" /></SelectTrigger>
                      <SelectContent>
                        {logs.map(l => (
                          <SelectItem key={l.id} value={l.id}>{l.opportunity_title || l.org_name} · {l.hours}h</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : !logsLoaded ? (
                    // deep-linked visitors ARE eligible — don't flash the
                    // locked note while their hour logs are still loading
                    <div data-testid="story-logs-loading-note" className="text-xs text-neutral-400 inline-flex items-center gap-2 px-1 py-2">
                      <Clock className="w-3.5 h-3.5 animate-pulse" /> Loading your contributions…
                    </div>
                  ) : (
                    <div data-testid="story-impact-locked-note" className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-forest/60" /> Impact stories unlock once your first hours are approved — the other two are open to everyone.
                    </div>
                  )
                )}
                <Textarea data-testid="story-text" rows={5} maxLength={1000} placeholder={
                  storyType === "impact" ? "What did you do? Who did you meet? What surprised you?"
                    : storyType === "neighbour_help" ? "Who did you help — or who helped you? What difference did it make?"
                    : "How did the gathering go? Who came, and what's the next one?"
                } value={text} onChange={(e) => setText(e.target.value)} />
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm text-forest cursor-pointer hover:underline">
                    <ImagePlus className="w-4 h-4" /> {uploading ? "Uploading…" : image ? "Change photo" : "Add a photo"}
                    <input data-testid="story-image-input" type="file" accept="image/*" className="hidden" onChange={onImage} />
                  </label>
                  {image && <img src={imgSrc(image)} alt="preview" className="h-12 w-12 object-cover rounded-lg border" />}
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="story-submit-btn" onClick={submit} disabled={storyType === "impact" && logs.length === 0} className="rounded-full bg-forest hover:bg-forest-hover">Publish</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : !user ? (
          <Button data-testid="stories-login-cta" variant="outline" className="rounded-full" onClick={() => nav("/login")}>Sign in to share yours</Button>
        ) : null}
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="stories-filter-chips">
        {FILTERS.map(([key, label]) => (
          <button
            key={key || "all"}
            type="button"
            data-testid={`stories-filter-${key || "all"}`}
            onClick={() => setFilter(key)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === key ? "bg-forest text-white border-forest" : "bg-white text-neutral-600 border-neutral-200 hover:border-forest/40"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-6">
        {stories.length === 0 && (
          <div className="text-center py-16 text-neutral-500 text-sm">No stories here yet — be the first to share one.</div>
        )}
        {stories.map((s, i) => (
          <StoryCard key={s.id} s={s} user={user} onLike={like} onDelete={remove} onPin={togglePin} i={i} highlighted={s.id === highlightId} />
        ))}
      </div>
    </div>
  );
}
